import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLiving } from './LivingContext';

const ThemeCtx = createContext({
    palette: 'classic',
    mode: 'dark',
    resolvedDark: true,
    experience: 'lightweight',
    setPalette: () => {},
    setMode: () => {},
    setExperience: () => {},
});

const PALETTE_KEY = 'gd_palette'; // 'classic' | 'forest'
const MODE_KEY = 'gd_mode';       // 'live' | 'light' | 'dark' | 'system'
const EXPERIENCE_KEY = 'gd_experience'; // 'lightweight' | 'full'
const PREFERENCES = {
    [PALETTE_KEY]: { values: ['classic', 'forest'], fallback: 'classic' },
    [MODE_KEY]: { values: ['live', 'light', 'dark', 'system'], fallback: 'dark' }, // dark until the user picks
    [EXPERIENCE_KEY]: { values: ['lightweight', 'full'], fallback: 'lightweight' },
};

function validPreference(key, value) {
    const { values, fallback } = PREFERENCES[key];
    return values.includes(value) ? value : fallback;
}

function readStored(key) {
    try { return validPreference(key, localStorage.getItem(key)); }
    catch { return PREFERENCES[key].fallback; }
}

function systemPrefersDark() {
    try { return window.matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; }
}

// 'live' follows the real sun at the user's location (see LivingContext).
export function applyTheme(palette, mode, sunDown = false) {
    const root = document.documentElement;
    root.classList.remove('theme-classic', 'theme-forest');
    root.classList.add(palette === 'forest' ? 'theme-forest' : 'theme-classic');
    const dark = mode === 'dark' || (mode === 'system' && systemPrefersDark()) || (mode === 'live' && sunDown);
    root.classList.toggle('dark', dark);
    // Tint the browser / Android status bar to match
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', getComputedStyle(root).getPropertyValue('--brand-bg').trim() || '#2C4C3B');
    return dark;
}

export function ThemeProvider({ children }) {
    const [palette, setPaletteState] = useState(() => readStored(PALETTE_KEY));
    const [mode, setModeState] = useState(() => readStored(MODE_KEY));
    const [resolvedDark, setResolvedDark] = useState(() => readStored(MODE_KEY) === 'dark');
    const [experience, setExperienceState] = useState(() => readStored(EXPERIENCE_KEY));

    // A presentation preference shared by GrowDaily and Eden, never an entitlement.
    // Keep existing lightweight/full values so saved choices survive upgrades.
    useEffect(() => {
        const sync = event => {
            try {
                if (event.storageArea && event.storageArea !== window.localStorage) return;
            } catch { return; }
            if (event.key === null || event.key === PALETTE_KEY) setPaletteState(event.key === null ? readStored(PALETTE_KEY) : validPreference(PALETTE_KEY, event.newValue));
            if (event.key === null || event.key === MODE_KEY) setModeState(event.key === null ? readStored(MODE_KEY) : validPreference(MODE_KEY, event.newValue));
            if (event.key === null || event.key === EXPERIENCE_KEY) setExperienceState(event.key === null ? readStored(EXPERIENCE_KEY) : validPreference(EXPERIENCE_KEY, event.newValue));
        };
        window.addEventListener('storage', sync);
        return () => window.removeEventListener('storage', sync);
    }, []);
    const { isNight } = useLiving();

    useEffect(() => {
        const dark = applyTheme(palette, mode, isNight);
        setResolvedDark(dark);
        // Remembered so index.html can paint the right theme before React loads
        try { localStorage.setItem('gd_live_dark', isNight ? '1' : '0'); } catch { /* ignore */ }
    }, [palette, mode, isNight]);

    // React to OS theme changes when in 'system' mode
    useEffect(() => {
        if (mode !== 'system') return;
        let mq;
        try { mq = window.matchMedia('(prefers-color-scheme: dark)'); } catch { return; }
        const handler = () => setResolvedDark(applyTheme(palette, 'system'));
        mq.addEventListener?.('change', handler);
        return () => mq.removeEventListener?.('change', handler);
    }, [mode, palette]);

    const setPalette = useCallback((p) => {
        const safe = validPreference(PALETTE_KEY, p);
        setPaletteState(safe);
        try { localStorage.setItem(PALETTE_KEY, safe); } catch { /* Keep working in memory. */ }
    }, []);

    const setMode = useCallback((m) => {
        const safe = validPreference(MODE_KEY, m);
        setModeState(safe);
        try { localStorage.setItem(MODE_KEY, safe); } catch { /* Keep working in memory. */ }
    }, []);

    const setExperience = useCallback((next) => {
        const safe = validPreference(EXPERIENCE_KEY, next);
        setExperienceState(safe);
        try { localStorage.setItem(EXPERIENCE_KEY, safe); } catch { /* ignore */ }
    }, []);

    const value = useMemo(
        () => ({ palette, mode, resolvedDark, experience, setPalette, setMode, setExperience }),
        [palette, mode, resolvedDark, experience, setPalette, setMode, setExperience],
    );

    return (
        <ThemeCtx.Provider value={value}>
            {children}
        </ThemeCtx.Provider>
    );
}

export function useTheme() { return useContext(ThemeCtx); }
